import FilterListIcon from '@mui/icons-material/FilterList';
import { Box } from '@mui/material';
import React, { useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { ClearFiltersButton, DepartmentFilter, FilterSelect, ViewSwitcher } from '../../../components/_mui';
import { DISTRIBUTOR, ORG_ADMIN, SUPER_ADMIN } from '../../../constants/users';
import useWindowSize from '../../../hooks/useWindowSize';
import { selectAdminType, selectIsAdminTypeUser } from '../../../store/selectors/auth';
import { IDepartment } from '../../../types/organizations';
import { ViewTypesEnum } from '../../../types/shell';
import { IFilterItem } from '../../../types/tables';
import { hasPermission } from '../../../utils/users';

interface IProps {
  renderAddButton: React.JSX.Element | null;
  filterConfig: IFilterItem[];
  activeView: ViewTypesEnum;
  setActiveView: (view: ViewTypesEnum) => void;
  Search: React.ReactNode;
  onClear?: () => void;
  onChange?: () => void;
  showClearButton?: boolean;
  selectedDepartments: string[];
  setSelectedDepartments: (departments: string[]) => void;
  allDepartments: IDepartment[];
}

const Filters = ({
  renderAddButton,
  filterConfig,
  activeView,
  setActiveView,
  onClear,
  onChange,
  Search,
  showClearButton,
  selectedDepartments,
  setSelectedDepartments,
  allDepartments,
}: IProps) => {
  const { mobile } = useWindowSize();
  const adminType = useSelector(selectAdminType);
  const isAdminTypeUser = useSelector(selectIsAdminTypeUser);

  const isDeptFilterShown = useMemo(
    () => isAdminTypeUser && hasPermission([ORG_ADMIN, SUPER_ADMIN, DISTRIBUTOR], adminType),
    [isAdminTypeUser, hasPermission, adminType],
  );

  const handleDepartmentChange = useCallback(
    (departments: string[]) => {
      setSelectedDepartments(departments);
      onChange?.();
    },
    [setSelectedDepartments, onChange],
  );

  const flexRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        {Search}
        {!mobile && renderAddButton}
      </Box>
      <Box sx={{ ...flexRowStyle, mt: 2.5 }}>
        <Box sx={flexRowStyle}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '30px' }}>
            <FilterListIcon color="disabled" />
            {filterConfig.map(({ tabId, label, value, onChange: onFilterChange, options }) => {
              return (
                <FilterSelect
                  key={tabId}
                  label={label}
                  value={value}
                  onChange={(newValue) => {
                    onFilterChange(newValue);
                    onChange?.();
                  }}
                  options={options}
                  onClear={() => {
                    onFilterChange('');
                    onChange?.();
                  }}
                />
              );
            })}
            {isDeptFilterShown && (
              <DepartmentFilter
                value={selectedDepartments}
                onChange={handleDepartmentChange}
                departments={allDepartments}
              />
            )}
            {showClearButton && onClear ? (
              <ClearFiltersButton
                onClick={() => {
                  onClear();
                  onChange?.();
                }}
              />
            ) : null}
          </Box>
        </Box>
        <ViewSwitcher activeView={activeView} setActiveView={setActiveView} />
      </Box>
    </Box>
  );
};

export default Filters;
